/**
 * Estado do Explorar mesas: uma lista por aba, o modo de ordenação (global para
 * todas as abas, como no original) e as ações de cartão (curtir, favoritar).
 */
import { create } from 'zustand';
import { api } from '../api.js';
import type { ExploreFacets, ExploreQueryInput, ExploreSort, ExploreTab, RoomCard } from '../shared.js';
import { useApp } from '../store.js';
import { toast } from '../ui/Toaster.js';

const SORT_KEY = 'rpgdesk.exploreSort';

function loadSort(): ExploreSort {
  try {
    return localStorage.getItem(SORT_KEY) === 'onlinePlayersQty' ? 'onlinePlayersQty' : 'recommendation';
  } catch {
    return 'recommendation';
  }
}

/** `counts`, `systems` e `languages` vêm junto com a lista (as contagens das abas, os sistemas e as línguas, com os mesmos filtros) */
type TabState = { rooms: RoomCard[]; loading: boolean; loadedAt: number | null } & Partial<ExploreFacets>;

type ExploreState = {
  sort: ExploreSort;
  tabs: Partial<Record<ExploreTab, TabState>>;
  setSort: (sort: ExploreSort) => void;
  load: (tab: ExploreTab, params?: ExploreQueryInput) => Promise<void>;
  /** troca um cartão em todas as abas (depois de curtir/favoritar) */
  patchCard: (card: RoomCard) => void;
  toggleFavorite: (card: RoomCard) => Promise<void>;
  vote: (card: RoomCard, value: -1 | 1) => Promise<void>;
};

/**
 * Cada leitura de uma aba ganha um número: a resposta de uma leitura que já ficou para trás (a busca
 * anterior, mais lenta) não cobre a de agora.
 */
const latest: Partial<Record<ExploreTab, number>> = {};
let reads = 0;

export const useExplore = create<ExploreState>()((set, get) => ({
  sort: loadSort(),
  tabs: {},
  setSort(sort) {
    try {
      localStorage.setItem(SORT_KEY, sort);
    } catch {
      // sem armazenamento: vale só nesta sessão
    }
    set({ sort });
  },
  async load(tab, params = {}) {
    const token = useApp.getState().token;
    if (!token) return;
    const mine = (latest[tab] = ++reads);
    // a resposta só vale se ainda for a leitura mais nova da aba, com a mesma sessão
    const current = () => latest[tab] === mine && useApp.getState().token === token;
    set((s) => ({ tabs: { ...s.tabs, [tab]: { ...s.tabs[tab], rooms: s.tabs[tab]?.rooms ?? [], loading: true, loadedAt: s.tabs[tab]?.loadedAt ?? null } } }));
    try {
      const { rooms, counts, systems, languages } = await api.explore(token, tab, { sort: get().sort, ...params });
      if (!current()) return;
      set((s) => ({ tabs: { ...s.tabs, [tab]: { rooms, counts, systems, languages, loading: false, loadedAt: Date.now() } } }));
    } catch (error) {
      if (!current()) return;
      set((s) => ({ tabs: { ...s.tabs, [tab]: { ...s.tabs[tab], rooms: s.tabs[tab]?.rooms ?? [], loading: false, loadedAt: s.tabs[tab]?.loadedAt ?? null } } }));
      toast(error instanceof Error ? error.message : String(error), 'error');
    }
  },
  patchCard(card) {
    set((s) => ({
      tabs: Object.fromEntries(
        Object.entries(s.tabs).map(([tab, state]) => [tab, { ...state!, rooms: state!.rooms.map((room) => (room.id === card.id ? { ...room, ...card } : room)) }]),
      ),
    }));
  },
  async toggleFavorite(card) {
    const token = useApp.getState().token;
    if (!token) return;
    try {
      const { room } = await api.setFavorite(token, card.id, !card.isFavorite);
      const patched = { ...room, onlineCount: card.onlineCount, ownerOnline: card.ownerOnline };
      get().patchCard(patched);
      // a lista de favoritas já carregada acompanha (o Início mostra a contagem dela): sai a desfavoritada,
      // e a recém-favoritada entra na frente
      if (room.isFavorite !== card.isFavorite) {
        set((s) => {
          if (!s.tabs.favorites) return s;
          const others = s.tabs.favorites.rooms.filter((r) => r.id !== room.id);
          return { tabs: { ...s.tabs, favorites: { ...s.tabs.favorites, rooms: room.isFavorite ? [patched, ...others] : others } } };
        });
      }
    } catch (error) {
      toast(error instanceof Error ? error.message : String(error), 'error');
    }
  },
  async vote(card, value) {
    const token = useApp.getState().token;
    if (!token) return;
    try {
      const { room } = await api.voteRoom(token, card.id, card.myVote === value ? 0 : value);
      get().patchCard({ ...room, onlineCount: card.onlineCount, ownerOnline: card.ownerOnline });
    } catch (error) {
      toast(error instanceof Error ? error.message : String(error), 'error');
    }
  },
}));

// as listas são da conta: ao sair (ou entrar outra pessoa nesta aba), as da anterior somem, e o que
// ainda estava carregando para ela fica de fora
useApp.subscribe((state, previous) => {
  if (state.user?.id === previous.user?.id) return;
  for (const tab of Object.keys(latest) as ExploreTab[]) latest[tab] = ++reads;
  useExplore.setState({ tabs: {} });
});

/**
 * Filtro de mesas +18 (Configurações → Geral): some com as marcadas como +18,
 * menos as minhas e as que eu frequento como mestre.
 */
export function withoutAdult<T extends { restrictedUnder18: boolean; ownerId: string; amIAuxGm?: boolean }>(rooms: T[], hide: boolean, me: string | undefined): T[] {
  return hide ? rooms.filter((r) => !r.restrictedUnder18 || r.ownerId === me || r.amIAuxGm) : rooms;
}
